import { utcToZonedTime } from 'date-fns-tz'
import type {
  DynamicPrepTimes,
  Hours,
  ServiceType,
  TimezonePython,
} from '@open-tender/types'

/** holidays[YYYY-MM-DD][serviceType] -> that date's hours override. */
export type HolidayHours = Record<
  string,
  Partial<Record<ServiceType, { open: string | null; close: string | null }>>
>

export interface ProjectedPickupArgs {
  cartSubtotal: number
  dynamicPrepTimes: DynamicPrepTimes | null | undefined
  hours: Hours | null | undefined
  holidays?: HolidayHours | null
  /** Queue-only wait per service type. Used for ASAP and walk-in; DO-230
   * leaves it out of the future pickup floor. */
  baseWaitTimes: Partial<Record<ServiceType, number | null>> | null | undefined
  /** The backend only enforces the guardrail for brands with this flag. */
  sendDynamicPrepTime: boolean
  timezone: TimezonePython | null | undefined
  serviceType: ServiceType | null | undefined
  requestedAt: string | 'asap' | null | undefined
  now?: Date
}

export interface ProjectedPickupResult {
  addedMinutes: number
  isPastClose: boolean
  reason: 'ok' | 'past_close' | 'unknown'
  /** Largest cart total still ready before close, when shrinking the cart
   * would help (backend: Order._closing_cart_cap). */
  maxCartTotal?: number | null
}

const WEEKDAYS = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
] as const

const timeToMinutes = (time: string): number => {
  const [hour, minute] = time.split(':').map(Number)
  return hour * 60 + minute
}

interface ServiceDay {
  open: number
  close: number
}

/**
 * That calendar day's open/close minutes, resolved the way the backend does
 * (models/orders.py _service_hours_for_day): a holiday override for the exact
 * date wins, otherwise the regular weekly row. Without this an extended
 * holiday schedule blocks valid orders and an early holiday close isn't
 * caught. Null when unknown or unrepresentable, so callers fail open.
 */
const resolveServiceDay = (
  local: Date,
  serviceType: ServiceType,
  hours: Hours | null | undefined,
  holidays: HolidayHours | null | undefined
): ServiceDay | null => {
  const pad = (n: number) => String(n).padStart(2, '0')
  const dateStr = `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(
    local.getDate()
  )}`
  let day: { open?: string | null; close?: string | null } | undefined =
    holidays?.[dateStr]?.[serviceType] ?? undefined
  if (!day) {
    day = hours?.[serviceType]?.find(
      (d) => d.weekday === WEEKDAYS[local.getDay()]
    )
  }
  if (!day || day.open == null || day.close == null) return null

  const open = timeToMinutes(day.open)
  let close = timeToMinutes(day.close)
  if (close === 0) close = 1440 // midnight close, see backend note
  if (close <= open) return null // midnight-crossing, unrepresentable
  return { open, close }
}

/**
 * Mirror of the backend's max_cart_total_before_close (models/orders.py): one
 * cent below the first tier whose extra minutes don't fit before close. Null
 * when even the smallest cart is late, or every tier fits.
 */
export const maxCartTotalBeforeClose = (
  dynamicPrepTimes: DynamicPrepTimes | null | undefined,
  readyMinWithoutTier: number,
  closeMin: number
): number | null => {
  const available = closeMin - readyMinWithoutTier
  if (available < 0) return null
  const tier = [...(dynamicPrepTimes ?? [])]
    .sort((a, b) => (a.min ?? 0) - (b.min ?? 0))
    .find((t) => (t.additional_time ?? 0) > available)
  if (!tier) return null
  const cap = Math.round(((tier.min ?? 0) - 0.01) * 100) / 100
  return cap > 0 ? cap : null
}

const unknown: ProjectedPickupResult = {
  addedMinutes: 0,
  isPastClose: false,
  reason: 'unknown',
}

/**
 * Client-side projection of the guardrail the backend enforces at
 * validate/submit (Order.validate_closing_time). Runs with zero network
 * calls so it can react to every cart quantity change -- it reads the
 * revenue-center payload the app already has in redux, the same one the
 * time picker itself reads from.
 *
 * Fails open (reason: 'unknown', isPastClose: false) whenever the inputs
 * don't give a confident answer, matching the backend's own fail-open
 * design (better/models/orders.py Order.validate_closing_time).
 */
export const projectPickupTime = ({
  cartSubtotal,
  dynamicPrepTimes,
  hours,
  holidays,
  baseWaitTimes,
  sendDynamicPrepTime,
  timezone,
  serviceType,
  requestedAt,
  now = new Date(),
}: ProjectedPickupArgs): ProjectedPickupResult => {
  // Without the flag the server never enforces the closing guardrail, so
  // projecting it here would block orders the server accepts.
  if (!sendDynamicPrepTime) return unknown
  if (!timezone || !serviceType) return unknown
  // The backend guardrail skips delivery (requested_at is the drop-off time
  // there, not when the kitchen must be ready, and the DSP quote replaces the
  // wait time later). Blocking it here would reject orders the server allows.
  if (serviceType === 'DELIVERY') return unknown

  const isAsap = !requestedAt || requestedAt.toLowerCase() === 'asap'

  // The tier match is always computed fresh from the live cart total (never
  // from a server field that can be stale or, worse, absent -- see below),
  // so the display figure ("needs about N more minutes") is always current.
  let addedMinutes = 0
  if (dynamicPrepTimes && dynamicPrepTimes.length > 0) {
    const tier = dynamicPrepTimes.find(
      (t) =>
        (t.min ?? -Infinity) <= cartSubtotal &&
        (t.max == null || cartSubtotal <= t.max)
    )
    addedMinutes = tier?.additional_time ?? 0
  }

  // Mirror the backend (Order._enforce_minimum_requested_at). DO-230 pickup
  // adds the prep to the customer's pick (a 05:15 pick with an 11-minute tier
  // is ready 05:26); walk-in keeps prep as a *floor* from now or opening.
  //
  // Prep = the tier for the *live* cart total, the DO-230 formula the server
  // uses for future pickup (no static kitchen prep). ASAP and walk-in are outside
  // it and still add the queue wait (Order._uses_do230_floor). wait_times is
  // not used: it has the tier for whatever cart total the location was last
  // fetched with baked in, and the menu page fetches it only once, so it goes
  // stale as the cart changes.
  const isDo230Floor = !isAsap && serviceType === 'PICKUP'
  let queueMs = 0
  if (!isDo230Floor) {
    const baseWait = baseWaitTimes?.[serviceType]
    if (baseWait == null) {
      return { addedMinutes, isPastClose: false, reason: 'unknown' }
    }
    queueMs = baseWait * 60_000
  }
  const prepMs = addedMinutes * 60_000 + queueMs
  const nowLocal = utcToZonedTime(now, timezone)

  let projectedMs: number
  let pickMs = nowLocal.getTime()
  if (isAsap) {
    projectedMs = nowLocal.getTime() + prepMs
  } else {
    const fromNow = nowLocal.getTime() + prepMs
    const requestedDt = new Date(requestedAt as string)
    if (Number.isNaN(requestedDt.getTime())) return unknown
    const requestedLocal = utcToZonedTime(requestedDt, timezone)
    // The kitchen can't start prepping before the store opens, so the floor
    // is the later of "prep started now" and "prep started at opening on the
    // requested day" (backend: Order._opening_floor_timestamp, DO-230).
    const requestedDay = resolveServiceDay(
      requestedLocal,
      serviceType,
      hours,
      holidays
    )
    const dayStartMs = new Date(
      requestedLocal.getFullYear(),
      requestedLocal.getMonth(),
      requestedLocal.getDate()
    ).getTime()
    const fromOpening = requestedDay
      ? dayStartMs + requestedDay.open * 60_000 + prepMs
      : fromNow
    // DO-230 pickup also counts the prep from the pick itself. Whole minutes
    // there, like the server: a floor carrying seconds from "now" is rounded
    // up instead of counting as a sub-minute push.
    const rawFloorMs = isDo230Floor
      ? Math.max(fromNow, fromOpening, requestedLocal.getTime() + prepMs)
      : Math.max(fromNow, fromOpening)
    const floorMs = isDo230Floor
      ? Math.ceil(rawFloorMs / 60_000) * 60_000
      : rawFloorMs
    // max(customer pick, floor) -- exactly what the backend enforces.
    projectedMs = Math.max(requestedLocal.getTime(), floorMs)
    pickMs = requestedLocal.getTime()
  }

  const projectedLocal = new Date(projectedMs)
  const day = resolveServiceDay(projectedLocal, serviceType, hours, holidays)
  if (!day) return { addedMinutes, isPastClose: false, reason: 'unknown' }

  const projectedMinutes =
    projectedLocal.getHours() * 60 + projectedLocal.getMinutes()
  const isPastClose = projectedMinutes > day.close

  // Same conditions as the backend's cap: a picked time (ASAP never gets one),
  // the tier is what pushed the order late, and the customer's own pick was
  // that day and inside hours.
  const pickLocal = new Date(pickMs)
  const pickMinutes = pickLocal.getHours() * 60 + pickLocal.getMinutes()
  const maxCartTotal =
    isPastClose &&
    !isAsap &&
    addedMinutes > 0 &&
    pickLocal.toDateString() === projectedLocal.toDateString() &&
    pickMinutes <= day.close
      ? maxCartTotalBeforeClose(
          dynamicPrepTimes,
          projectedMinutes - addedMinutes,
          day.close
        )
      : null

  return {
    addedMinutes,
    isPastClose,
    reason: isPastClose ? 'past_close' : 'ok',
    maxCartTotal,
  }
}
